import { ReactElement, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Component as PCLogo } from 'consumer/components/logos/pclogo-black.svg';
import { Component as SearchIcon } from 'icons/icon-ui--search.svg';
import { parseZone } from 'moment';
import { Route } from '../../utils/config';
import { useMenuWithCart } from '../../contexts/MenuWithCart';
import { AccountMenu } from '../account-menu/AccountMenu';
import MenuSelectorContainer from '../menu-selector/MenuSelectorContainer';
import CartDrawerContainer from '../cart-drawer/CartDrawerContainer';
import { CouponTooltip } from '../coupon-tooltip/CouponTooltip';
import { HeaderSidebar } from './HeaderSidebar';
import Link from '../shared/link/Link';
import styles from './Header.module.scss';
import { HeaderSearch } from './HeaderSearch';

type Props = {
  withCart?: boolean;
  isPDP?: boolean;
}

export function LoggedInHeader({
  withCart = false,
  isPDP = false,
}: Props): ReactElement {
  const navigate = useNavigate();
  const {
    cart,
    menu,
  } = useMenuWithCart();
  const {
    couponAmount,
    couponMessage,
  } = cart?.priceSummary || {};
  const selectedMenuDate = menu?.deliveryOn ? parseZone(menu?.deliveryOn)
    .format('ddd, MMM D') : '';
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  return (
    <header role="banner" className={styles.header}>
      <div className={styles.header_content}>
        <div className={styles.sidebar_and_search}>
          <HeaderSidebar />
          <button
            type="button"
            className={styles.mobile_search_icon}
            aria-label="Search"
            onClick={() => setMobileSearchOpen((o) => !o)}
          >
            <SearchIcon width={20} height={20} />
          </button>
        </div>

        <div className={styles.logo_and_search}>
          <a
            title="Purple Carrot"
            className={`${styles.pc_logo} ${styles.logged_in}`}
            data-cy="pc-logo"
            aria-label="Home"
            href="/upcoming-menus"
            onClick={(e) => {
              e.preventDefault();
              navigate('/upcoming-menus');
            }}
            tabIndex={0}
          >
            <PCLogo width="135" focusable="false" />
          </a>
        </div>

        <div className={styles.header_search_center}>
          <HeaderSearch isLoggedIn />
        </div>

        <nav aria-label="Logged in site navigation">
          <ul className={styles.nav_container}>
            {couponAmount && (
              <li className={`${styles.nav_item} ${styles.coupon_tooltip}`}>
                <CouponTooltip couponAmount={couponAmount} tooltipMessage={couponMessage} />
              </li>
            )}

            <li className={`${styles.nav_item} ${styles.menu_dropdown}`}>
              {withCart && !isPDP ? (
                <MenuSelectorContainer />
              ) : (
                <Link
                  href={Route.HUB}
                  data-heap-id="header-menu-hub"
                  className={({ isActive }) => (isActive ? styles.is_active : '')}
                  style={{ color: '#000000' }}
                >
                  {`${selectedMenuDate} Menu`}
                </Link>
              )}
            </li>

            <div className={styles.nav_links}>
              <li>
                <Link
                  href={Route.ORDER_HISTORY}
                  data-heap-id="header-order-history"
                  className={({ isActive }) => (`${styles.nav_item} ${isActive ? styles.is_active : ''}`)}
                >
                  Order History
                </Link>
              </li>
              <li>
                <Link
                  href={Route.TRACKING}
                  data-heap-id="header-track-package"
                  className={({ isActive }) => (`${styles.nav_item} ${isActive ? styles.is_active : ''}`)}
                >
                  Track Package
                </Link>
              </li>
            </div>

            <li className={styles.nav_item}>
              <AccountMenu />
            </li>

            {withCart && (
              <li className={styles.nav_item}>
                <CartDrawerContainer key={cart?.id} />
              </li>
            )}
          </ul>
        </nav>
      </div>

      {mobileSearchOpen && (
        <div className={styles.mobile_search_dropdown}>
          <HeaderSearch isLoggedIn autoFocus onBlur={() => setMobileSearchOpen(false)} />
        </div>
      )}
    </header>
  );
}
